import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Options } from "@hey-api/client-fetch";
import { toast } from "sonner";
import { postV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeMutation } from "../../client/@tanstack/react-query.gen";
import type {
  PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeData,
  PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeResponse,
  PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeError,
} from "../../client";
import { getOneProfileQueryKey } from "./get-one-profile";

interface UploadResumeData {
  profileId: string;
  file: File;
}

const mutationKey = ["uploadResume"];

export const useUploadResume = () => {
  const queryClient = useQueryClient();

  return useMutation<
    PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeResponse,
    PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeError,
    Options<PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeData>
  >({
    ...postV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeMutation(),
    mutationKey,
    onSuccess: async (response, variables) => {
      // Invalidate the profile query to refresh data after parsing completes
      // Note: The actual profile update happens asynchronously on the server
      await queryClient.invalidateQueries({
        queryKey: getOneProfileQueryKey(variables.path.profileID),
        exact: true,
      });
    },
    onError: (error) => {
      toast.error(`Failed to upload resume: ${error}`);
    },
  });
};

// Helper function to create properly typed options for the mutation
export const createUploadResumeOptions = (
  profileId: string,
  organizationId: string,
  file: File,
): Options<PostV1OrganizationsByOrganizationIdProfilesByProfileIdUploadResumeData> => ({
  path: {
    profileID: profileId,
    organizationID: organizationId,
  },
  body: {
    resume: file,
  },
});
